import { makeStyles, useMediaQuery, Hidden } from '@material-ui/core';
import { useState, useCallback, useMemo, memo, useRef, useEffect } from 'react';
import moment from 'moment';
import CalendarRow from './CalendarRow';
import 'moment/locale/sv';
import { getToday } from 'components/Home/Forecast/utils';
import { isWeekend, adjustedDayLength } from './utils/calendarPositioning';

const useStyles = makeStyles((theme) => ({
  calendar_container: {
    width: '100%',
    maxHeight: '88vh',
    height: '1000px',
    backgroundColor: 'white',
    position: 'relative',
    overflow: 'hidden',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },
  scroll_wrapper: {
    overflow: 'auto',
    width: '100%',
    height: '100%',
    scrollbarWidth: 'thin',
    isolation: 'isolate',
    overscrollBehavior: 'none',
  },
  scroll_content: {
    position: 'relative',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  rows_spacer: {
    flex: 1,
    minHeight: 20,
    position: 'relative',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      minHeight: 300,
    },
  },
  rows_spacer_user_col: {
    position: 'sticky',
    left: 0,
    width: 200,
    flexShrink: 0,
    backgroundColor: 'white',
    zIndex: 10,
    borderRight: '1px solid #E5E5E5',
    [theme.breakpoints.down('sm')]: {
      width: 100,
    },
  },
  header_row: {
    display: 'flex',
    position: 'sticky',
    top: 0,
    zIndex: 15,
    backgroundColor: 'white',
  },
  header_user_spacer: {
    position: 'sticky',
    left: 0,
    zIndex: 16,
    backgroundColor: 'white',
    flexShrink: 0,
    width: 200,
    [theme.breakpoints.down('sm')]: {
      width: 100,
    },
  },
  header_calendar_area: {
    flex: 1,
    minWidth: 0,
  },
  lines_container: {
    display: 'flex',
    height: '100%',
  },
  days_container: {
    display: 'flex',
  },
  day: {
    flex: 1,
    fontSize: '11px',
    padding: '2px 0',
    fontWeight: 600,
    textAlign: 'center',
    borderBottom: '0.5px solid #E5E5E5',
    zIndex: 2,
    transition: 'background-color 0.1s ease',
    [theme.breakpoints.down('sm')]: {
      fontSize: '10px',
    },
  },
  dayHighlighted: {
    backgroundColor: 'rgba(33, 150, 243, 0.15) !important',
    color: '#1976d2 !important',
    zIndex: 50,
    '& span': {
      color: '#1976d2 !important',
    },
  },
  dayHighlightedStart: {
    borderRadius: '8px 0 0 8px',
  },
  dayHighlightedEnd: {
    borderRadius: '0 8px 8px 0',
  },
  day_subtitle: {
    fontSize: 8, fontWeight: 400, color: '#333', [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  day_weekend: {
    flex: 0.15,
    fontSize: 0,
    borderBottom: 0,
    opacity: 0,
    '& span': {
      display: 'none',
    },
  },
  today: {
    fontWeight: 700,
    backgroundColor: 'rgba(80, 98, 112, 0.15) !important',
  },
  months_container: {
    display: 'flex',
    zIndex: 2,
  },
  month: {
    textAlign: 'left',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '4px',
    borderBottom: '0.5px solid #E5E5E5',
    zIndex: 2,
    fontSize: '14px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '13px',
    },
  },
  line_container: {
    flex: 1,
    backgroundColor: '#FFF',
  },
  line_weekend_container: {
    backgroundColor: '#F9FAFB',
    flex: 0.15,
  },
  lines_outer_container: {
    position: 'absolute',
    left: 200,
    right: 0,
    top: 46,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 0,
    [theme.breakpoints.down('sm')]: {
      left: 100,
    },
  },
  line: {
    height: '100%',
    width: '1px',
    backgroundColor: '#E5E5E5',
    flex: 1,
  },
  line_month: {
    backgroundColor: '#E8E8E8',
    width: 2,
    marginTop: -30,
    height: 'calc(100% + 30px)',
  },
  calendarRow: {
    height: '45px',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.1s ease',
    position: 'relative',
    contentVisibility: 'auto',
    containIntrinsicBlockSize: '45px',
    [theme.breakpoints.down('sm')]: {
      height: '50px',
      containIntrinsicBlockSize: '50px',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      contentVisibility: 'visible',
      zIndex: 1,
    },
    '&:hover $calendarRowUserItem': {
      backgroundColor: '#F5F5F5',
    },
  },
  calendarRowUserItem: {
    position: 'sticky',
    left: 0,
    width: 200,
    flexShrink: 0,
    zIndex: 10,
    backgroundColor: 'white',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 4,
    borderRight: '1px solid #E5E5E5',
    [theme.breakpoints.down('sm')]: {
      width: 100,
    },
  },
  user_image: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    marginRight: 10,
    flexShrink: 0,
  },
  user_letter_image: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    marginRight: 10,
    flexShrink: 0,
    backgroundColor: theme.palette.gray.g6,
    color: theme.palette.gray.g1,
    fontWeight: 600,
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  user_text_container: {
    minWidth: 0,
    flex: 1,
  },
  user_name: {
    color: theme.palette.gray.g1,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: '18px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  user_title: {
    color: theme.palette.gray.g2,
    fontWeight: 400,
    fontSize: 10,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      fontSize: 8,
    },
  },
  calendarRowContainer: {
    flex: 1,
    position: 'relative',
  },
}));

export interface CalendarItemType {
  startDate: moment.Moment;
  endDate?: moment.Moment;
  title: string;
  comment?: string | null;
  status?: string;
  externalSource?: string | null;
  _id?: string | null;
  isCurrentWorkloadPlaceholder?: boolean;
  wfhIndex?: number;
}

export type CalendarUserType = {
  isCurrentUser: boolean;
  id: string | null | undefined;
  name: string | null | undefined;
  currentWorkload: number | null | undefined;
  title: string | null | undefined;
  profilePicture: string | null | undefined;
  items: CalendarItemType[];
};

// Memoized Day Header component to prevent unnecessary re-renders
const DayHeader = memo(({
  day,
  startDate,
  isToday,
  isWeekend,
  shouldHighlight,
  isSingleDay,
  isStart,
  isEnd,
  classes
}: {
  day: number;
  startDate: moment.Moment;
  isToday: boolean;
  isWeekend: boolean;
  shouldHighlight: boolean;
  isSingleDay: boolean;
  isStart: boolean;
  isEnd: boolean;
  classes: any;
}) => {
  const dayClasses = [classes.day];

  if (shouldHighlight) {
    dayClasses.push(classes.dayHighlighted);
    if (isSingleDay || isStart) dayClasses.push(classes.dayHighlightedStart);
    if (isSingleDay || isEnd) dayClasses.push(classes.dayHighlightedEnd);
  } else if (isWeekend) {
    dayClasses.push(classes.day_weekend);
  } else if (isToday) {
    dayClasses.push(classes.today);
  }

  return (
    <div className={dayClasses.join(' ')}>
      <span className={classes.day_subtitle}>
        {moment(startDate).locale('en').add(day, 'days').format('dd')}
      </span>
      {' '}
      {moment(startDate).locale('en').add(day, 'days').format('D')}
    </div>
  );
});

// Custom comparator: only re-render if the actual data changed
const areRowPropsEqual = (
  prev: CalendarRowWithUserProps,
  next: CalendarRowWithUserProps
): boolean => {
  // Check simple props
  if (prev.isVisible !== next.isVisible) return false;
  if (prev.isAdmin !== next.isAdmin) return false;
  if (prev.viewOnly !== next.viewOnly) return false;
  if (prev.length !== next.length) return false;
  if (prev.dayPixelWidth !== next.dayPixelWidth) return false;

  // Check user identity and display data
  if (prev.user.id !== next.user.id) return false;
  if (prev.user.name !== next.user.name) return false;
  if (prev.user.title !== next.user.title) return false;
  if (prev.user.profilePicture !== next.user.profilePicture) return false;
  if (prev.user.currentWorkload !== next.user.currentWorkload) return false;

  // Check items (the forecast data) - this is the key comparison
  const prevItems = prev.user.items;
  const nextItems = next.user.items;
  if (prevItems.length !== nextItems.length) return false;
  for (let i = 0; i < prevItems.length; i++) {
    const a = prevItems[i];
    const b = nextItems[i];
    if (a._id !== b._id) return false;
    if (a.status !== b.status) return false;
    if (a.startDate.valueOf() !== b.startDate.valueOf()) return false;
    if ((a.endDate?.valueOf() ?? 0) !== (b.endDate?.valueOf() ?? 0)) return false;
    if (a.comment !== b.comment) return false;
    if (a.externalSource !== b.externalSource) return false;
  }

  // Function refs and classes are stable (useCallback / makeStyles), skip them
  return true;
};

type CalendarRowWithUserProps = {
  user: CalendarUserType;
  isVisible: boolean;
  startDate: moment.Moment;
  length: number;
  dayPixelWidth: number;
  editItem: (item: CalendarItemType, userId: string) => void;
  onDragCreate?: (userId: string, startDate: Date, endDate: Date) => void;
  onDragHighlight: (highlight: { startDayIndex: number; endDayIndex: number } | null) => void;
  onDragMove?: (itemId: string, startDate: Date, endDate: Date) => void;
  onDragResize?: (itemId: string, startDate: Date, endDate: Date) => void;
  isAdmin: boolean;
  viewOnly: boolean;
  classes: any;
};

// Row with user info + calendar content (only renders CalendarRow when visible)
const CalendarRowWithUser = memo(({
  user,
  isVisible,
  startDate,
  length,
  dayPixelWidth,
  editItem,
  onDragCreate,
  onDragHighlight,
  onDragMove,
  onDragResize,
  isAdmin,
  viewOnly,
  classes
}: CalendarRowWithUserProps) => {
  return (
    <div className={classes.calendarRow}>
      <div className={classes.calendarRowUserItem}>
        <Hidden smDown>
          {user.profilePicture ? (
            <img
              className={classes.user_image}
              alt={`${user.name}`}
              src={'https://d378wr66tg5qg7.cloudfront.net/' + user.profilePicture}
            />
          ) : (
            <div className={classes.user_letter_image}>{user.name?.charAt(0)}</div>
          )}
        </Hidden>
        <div className={classes.user_text_container}>
          <div className={classes.user_name}>{user.name}</div>
          <div className={classes.user_title}>{user.title}</div>
        </div>
      </div>
      <div className={classes.calendarRowContainer}>
        {isVisible && (
          <CalendarRow
            user={user}
            startDate={startDate}
            length={length}
            dayPixelWidth={dayPixelWidth}
            edit={editItem}
            viewOnly={viewOnly}
            isAdmin={isAdmin}
            onDragCreate={onDragCreate}
            onDragHighlight={onDragHighlight}
            onDragMove={onDragMove}
            onDragResize={onDragResize}
          />
        )}
      </div>
    </div>
  );
}, areRowPropsEqual);

type CalendarProps = {
  users: CalendarUserType[] | null | undefined;
  startDate: moment.Moment;
  length: number;
  viewportLength: number;
  editItem: (item: CalendarItemType, userId: string) => void;
  onDragCreate?: (userId: string, startDate: Date, endDate: Date) => void;
  onDragMove?: (itemId: string, startDate: Date, endDate: Date) => void;
  onDragResize?: (itemId: string, startDate: Date, endDate: Date) => void;
  isAdmin: boolean;
  viewOnly: boolean;
};

const Calendar = memo(({
  users,
  startDate,
  length,
  viewportLength,
  editItem,
  onDragCreate,
  onDragMove,
  onDragResize,
  isAdmin,
  viewOnly
}: CalendarProps) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width:600px)');
  const userColumnWidth = isMobile ? 100 : 200;

  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
  const visibleRangeRef = useRef({ start: 0, end: 50 });
  // Measure container width
  useEffect(() => {
    const el = scrollWrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Track visible rows based on scroll position
  useEffect(() => {
    const el = scrollWrapperRef.current;
    if (!el) return;
    const buffer = 20;
    const rowHeight = isMobile ? 50 : 45;

    const updateVisibleRange = () => {
      const scrollTop = el.scrollTop;
      const viewportHeight = el.clientHeight;
      const start = Math.max(0, Math.floor(scrollTop / rowHeight) - buffer);
      const end = Math.min(
        (users?.length || 1) - 1,
        Math.ceil((scrollTop + viewportHeight) / rowHeight) + buffer
      );
      if (start !== visibleRangeRef.current.start || end !== visibleRangeRef.current.end) {
        visibleRangeRef.current = { start, end };
        setVisibleRange({ start, end });
      }
    };

    updateVisibleRange();
    el.addEventListener('scroll', updateVisibleRange, { passive: true });
    return () => el.removeEventListener('scroll', updateVisibleRange);
  }, [users?.length]);

  // Calculate scroll content width so each day is the same pixel width as before
  const calendarAreaWidth = containerWidth - userColumnWidth;
  const dayPixelWidth = containerWidth > 0 ? calendarAreaWidth / viewportLength : 0;
  const scrollContentWidth = containerWidth > 0
    ? userColumnWidth + dayPixelWidth * length
    : undefined;

  // State for drag highlighting
  const [dragHighlight, setDragHighlight] = useState<{
    startDayIndex: number;
    endDayIndex: number;
  } | null>(null);

  const handleDragHighlight = useCallback((highlight: { startDayIndex: number; endDayIndex: number } | null) => {
    setDragHighlight(highlight);
  }, []);

  const months = useMemo(() => {
    const result = [{ title: moment(startDate).locale("en").format('MMMM'), length: 0 }];

    for (let i = 0; i < length; i++) {
      if (moment(startDate).add(i, 'days').date() === 1)
        result.push({
          title: moment(startDate).locale("en").add(i, 'days').format('MMMM'),
          length: adjustedDayLength(startDate, i),
        });
      else result[result.length - 1].length += adjustedDayLength(startDate, i);
    }

    return result;
  }, [startDate, length]);

  const dayHeaders = useMemo(() => {
    const today = moment(getToday());

    return Array.from({ length }, (_, i) => {
      const dayMoment = moment(startDate).add(i, 'days');
      const isToday = dayMoment.isSame(today, 'day');
      const isWeekendDay = isWeekend(startDate, i);

      return {
        day: i,
        isToday,
        isWeekend: isWeekendDay,
        dayMoment,
      };
    });
  }, [startDate, length]);

  const highlightCalculations = useMemo(() => {
    if (!dragHighlight) return {};

    const result: Record<number, {
      shouldHighlight: boolean;
      isSingleDay: boolean;
      isStart: boolean;
      isEnd: boolean;
    }> = {};
    const { startDayIndex, endDayIndex } = dragHighlight;

    let firstWeekdayIndex = -1;
    let lastWeekdayIndex = -1;
    const weekdayIndices: number[] = [];

    for (let i = startDayIndex; i <= endDayIndex; i++) {
      const dayHeader = dayHeaders[i];
      if (dayHeader && !dayHeader.isWeekend) {
        weekdayIndices.push(i);
        if (firstWeekdayIndex === -1) firstWeekdayIndex = i;
        lastWeekdayIndex = i;
      }
    }

    const isSingleDay = weekdayIndices.length === 1;

    for (const i of weekdayIndices) {
      result[i] = {
        shouldHighlight: true,
        isSingleDay,
        isStart: i === firstWeekdayIndex,
        isEnd: i === lastWeekdayIndex,
      };
    }

    return result;
  }, [dragHighlight, dayHeaders]);

  const userList = users || [];

  return (
    <>
      <div className={classes.calendar_container}>
        <div className={classes.scroll_wrapper} ref={scrollWrapperRef}>
          <div
            className={classes.scroll_content}
            style={scrollContentWidth ? { width: scrollContentWidth } : undefined}
          >
            {/* Grid lines */}
            <div className={classes.lines_outer_container}>
              <div className={classes.lines_container}>
                {dayHeaders.map((dayHeader, i) => {
                  const isFirstWeekdayAfterWeekend = i > 0 && !dayHeader.isWeekend && dayHeaders[i - 1].isWeekend;
                  const isFirstDayOfMonth = dayHeader.dayMoment.date() === 1;
                  const shouldShowLine = !dayHeader.isWeekend && (!isFirstWeekdayAfterWeekend || isFirstDayOfMonth);

                  return (
                    <div
                      key={i}
                      className={
                        classes.line_container +
                        ' ' +
                        (dayHeader.isWeekend ? classes.line_weekend_container : '')
                      }
                    >
                      {shouldShowLine && (
                        <div className={`${classes.line} ${isFirstDayOfMonth ? classes.line_month : ''}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Header area - sticky at top during vertical scroll */}
            <div className={classes.header_row}>
              <div className={classes.header_user_spacer} />
              <div className={classes.header_calendar_area}>
                <div className={classes.months_container}>
                  {months.map((month, index) => (
                    <div key={index} className={classes.month} style={{ flex: month.length }}>
                      {month.length > 5 && month.title}
                    </div>
                  ))}
                </div>

                <div className={classes.days_container}>
                  {dayHeaders.map((dayHeader, i) => {
                    const highlight = highlightCalculations[i] || {
                      shouldHighlight: false,
                      isSingleDay: false,
                      isStart: false,
                      isEnd: false,
                    };

                    return (
                      <DayHeader
                        key={i}
                        day={i}
                        startDate={startDate}
                        isToday={dayHeader.isToday}
                        isWeekend={dayHeader.isWeekend}
                        shouldHighlight={highlight.shouldHighlight}
                        isSingleDay={highlight.isSingleDay}
                        isStart={highlight.isStart}
                        isEnd={highlight.isEnd}
                        classes={classes}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* User rows */}
            {userList.map((user, index) => (
              <CalendarRowWithUser
                key={user.id}
                user={user}
                isVisible={index >= visibleRange.start && index <= visibleRange.end}
                startDate={startDate}
                length={length}
                dayPixelWidth={dayPixelWidth}
                editItem={editItem}
                onDragCreate={onDragCreate}
                onDragHighlight={handleDragHighlight}
                onDragMove={onDragMove}
                onDragResize={onDragResize}
                isAdmin={isAdmin}
                viewOnly={viewOnly}
                classes={classes}
              />
            ))}

            {/* Extend user column background below last row */}
            <div className={classes.rows_spacer}>
              <div className={classes.rows_spacer_user_col} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default Calendar;
